// P54 — Page Admin Backups (réservée au responsable)
// Historique des dumps PostgreSQL, backup manuel, téléchargement, restauration.
//
// ⚠️ La restauration ÉCRASE la base (schéma + données + identifiants).
//    Une confirmation explicite est demandée via ConfirmDialog.
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Download, HardDriveDownload, RotateCcw, Trash2, Play, AlertTriangle, Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/components/ui/toast";
import { useT } from "@/lib/i18n";
import { useCan } from "@/hooks/useCan";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

interface BackupItem {
  name: string;
  size_bytes: number;
  size_human: string;
  created_at: string;
}

interface BackupListResponse {
  items: BackupItem[];
  total: number;
  keep_count: number;
  directory: string;
}

interface BackupStatus {
  enabled: boolean;
  running: boolean;
  is_owner: boolean;
  schedule_time: string;
  timezone: string;
  interval_seconds: number;
  keep_count: number;
  directory: string;
  next_run_at: string | null;
  last_run_at: string | null;
  last_error: string | null;
  runs_count: number;
  pg_tools_available: boolean;
}

export default function BackupsPage() {
  const t = useT();
  const qc = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["backups"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/backups`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as BackupListResponse;
    },
  });

  const statusQuery = useQuery({
    queryKey: ["backups-status"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/backups/status`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as BackupStatus;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/v1/backups`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      return (await res.json()) as BackupItem;
    },
    onSuccess: (info) => {
      toast({
        title: t("backup.toast.created"),
        description: `${info.name} (${info.size_human})`,
        variant: "success",
      });
      qc.invalidateQueries({ queryKey: ["backups"] });
      qc.invalidateQueries({ queryKey: ["backups-status"] });
    },
    onError: (err) => {
      toast({
        title: t("backup.toast.error"),
        description: (err as Error).message,
        variant: "error",
        duration: 8000,
      });
    },
  });

  // D100 — import d'un dump .sql.gz uploadé depuis le navigateur.
  // ⚠️ LE FICHIER EST UPLOADÉ MAIS LA BASE N'EST PAS RESTAURÉE.
  //    L'utilisateur doit ensuite cliquer sur le bouton « Restaurer »
  //    de l'entrée créée (même popup destructive que les backups existants).
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const res = await fetch(`${API_BASE}/v1/backups/import`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.text();
        let detail = body || `HTTP ${res.status}`;
        try {
          const j = JSON.parse(body);
          if (j.detail) detail = j.detail;
        } catch { /* pas JSON */ }
        throw new Error(detail);
      }
      return (await res.json()) as { filename: string; size_bytes: number; warnings: string[]; restore_endpoint: string };
    },
    onSuccess: (data) => {
      toast({
        title: t("backup.import_uploaded"),
        description: data.filename,
        variant: "success",
        duration: 12000,
      });
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
    onError: (err) => {
      toast({
        title: t("backup.toast.error"),
        description: (err as Error).message,
        variant: "error",
        duration: 10000,
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`${API_BASE}/v1/backups/${encodeURIComponent(name)}/restore`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      return (await res.json()) as { name: string; warning: string };
    },
    onSuccess: (data) => {
      toast({
        title: t("backup.toast.restored"),
        description: data.warning,
        variant: "success",
        duration: 10000,
      });
      setRestoreTarget(null);
      qc.invalidateQueries({ queryKey: ["backups"] });
      qc.invalidateQueries({ queryKey: ["backups-status"] });
    },
    onError: (err) => {
      toast({
        title: t("backup.toast.error"),
        description: (err as Error).message,
        variant: "error",
        duration: 10000,
      });
      setRestoreTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`${API_BASE}/v1/backups/${encodeURIComponent(name)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) {
        throw new Error(`HTTP ${res.status}`);
      }
    },
    onSuccess: () => {
      toast({ title: t("backup.toast.deleted"), variant: "success" });
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
    onError: (err) => {
      toast({
        title: t("backup.toast.error"),
        description: (err as Error).message,
        variant: "error",
      });
      setDeleteTarget(null);
    },
  });

  const status = statusQuery.data;
  const backups = listQuery.data?.items ?? [];
  const canAccess = useCan("admin");

  // 🛡️ Section réservée au responsable (respo+)
  if (!canAccess) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-amber-500" />
            <p>{ht("Vous n'avez pas les droits pour accéder à cette page.")}</p>
            <p className="text-xs">{ht("Réservée au rôle Responsable ou Admin système.")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("backup.title")}</h1>
          <p className="text-muted-foreground">{t("backup.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            data-testid="backup-create-button"
            variant="default"
          >
            <Play className="mr-1 h-4 w-4" />
            {createMutation.isPending ? t("backup.creating") : t("backup.create_now")}
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={importMutation.isPending}
            data-testid="backup-import-button"
            variant="outline"
          >
            <Upload className="mr-1 h-4 w-4" />
            {importMutation.isPending ? t("backup.import_uploading") : t("backup.import_button")}
          </Button>
        </div>
      </div>

      {/* ═══ D100 — Import d'un dump uploadé ═══════════════════════════════════════ */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".sql.gz,application/gzip,application/x-gzip"
        className="hidden"
        data-testid="backup-import-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) importMutation.mutate(f);
          e.target.value = "";  // permet de re-uploader le même fichier
        }}
      />

      {/* ── État du scheduler ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            {t("backup.scheduler_title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">{t("backup.schedule_time")}</div>
            <div className="font-semibold" data-testid="backup-schedule-time">
              {status?.schedule_time ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t("backup.timezone")}</div>
            <div className="font-semibold" data-testid="backup-timezone">
              {status?.timezone ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t("backup.next_run")}</div>
            <div className="font-semibold" data-testid="backup-next-run">
              {status?.next_run_at
                ? new Date(status.next_run_at).toLocaleString("fr-FR")
                : t("backup.interval_mode")}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t("backup.keep_count")}</div>
            <div className="font-semibold" data-testid="backup-keep-count">
              {status?.keep_count ?? "—"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Historique ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDriveDownload className="h-4 w-4" />
            {t("backup.history_title")} ({backups.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : backups.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="backup-empty">
              {t("backup.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left">{t("backup.col.name")}</th>
                    <th className="px-3 py-2 text-left">{t("backup.col.date")}</th>
                    <th className="px-3 py-2 text-right">{t("backup.col.size")}</th>
                    <th className="px-3 py-2 text-right">{t("backup.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b.name} className="border-b border-border/50" data-testid="backup-row">
                      <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                      <td className="px-3 py-2">
                        {new Date(b.created_at).toLocaleString("fr-FR")}
                      </td>
                      <td className="px-3 py-2 text-right">{b.size_human}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <a
                            href={`${API_BASE}/v1/backups/${encodeURIComponent(b.name)}/download`}
                            download={b.name}
                            data-testid={`backup-download-${b.name}`}
                            title={t("backup.action.download")}
                          >
                            <Button variant="outline" size="sm">
                              <Download className="mr-1 h-3.5 w-3.5" />
                              {t("backup.action.download")}
                            </Button>
                          </a>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRestoreTarget(b.name)}
                            data-testid={`backup-restore-${b.name}`}
                            title={t("backup.action.restore")}
                          >
                            <RotateCcw className="mr-1 h-3.5 w-3.5" />
                            {t("backup.action.restore")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(b.name)}
                            data-testid={`backup-delete-${b.name}`}
                            title={t("backup.action.delete")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Confirmation de restauration (⚠️ destructif) ── */}
      <ConfirmDialog
        open={restoreTarget !== null}
        title={t("backup.restore_confirm_title")}
        description={
          restoreTarget
            ? t("backup.restore_confirm_desc").replace("{name}", restoreTarget)
            : ""
        }
        confirmLabel={t("backup.restore_confirm_label")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        loading={restoreMutation.isPending}
        onConfirm={() => {
          if (restoreTarget) restoreMutation.mutate(restoreTarget);
        }}
        onCancel={() => setRestoreTarget(null)}
      />

      {/* ── Confirmation de suppression ── */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={t("backup.delete_confirm_title")}
        description={
          deleteTarget ? t("backup.delete_confirm_desc").replace("{name}", deleteTarget) : ""
        }
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
